import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { FaMap } from 'react-icons/fa';
import MapboxMap from '../components/MapboxMap';
import GraveLocator from '../components/GraveLocator';
import TimeOfDaySelector from '../components/TimeOfDaySelector';
import './MapboxGuestMapView.css';
import '../components/BookmarkManager.css';

export default function MapboxGuestMapView() {
  const [lots, setLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, available, reserved, occupied
  const [all3DGardenLots, setAll3DGardenLots] = useState([]);
  
  // Navigation state from MapboxMap
  const [navigationState, setNavigationState] = useState({
    isNavigating: false,
    navigationRoute: null,
    currentStep: 0
  });
  
  // Time of day selector state
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState('dawn');
  const [isColumbariumGridShown, setIsColumbariumGridShown] = useState(false);
  
  // Ref to access MapboxMap functions
  const mapboxMapRef = useRef(null);

  // Add error timeout cleanup
  useEffect(() => {
    let errorTimeout;
    if (error) {
      errorTimeout = setTimeout(() => {
        setError(null);
      }, 5000);
    }
    return () => {
      if (errorTimeout) {
        clearTimeout(errorTimeout);
      }
    };
  }, [error]);

  // Fetch graves from backend (public endpoint)
  // Garden polygon coordinates (exact from your GeoJSON data)
  const gardenPolygons = {
    'A': [
      [121.069415, 14.53879],
      [121.069888, 14.538776],
      [121.069876, 14.538393],
      [121.069403, 14.538394],
      [121.069415, 14.53879] // Close the polygon
    ],
    'B': [
      [121.069928, 14.538777],
      [121.070611, 14.538762],
      [121.070609, 14.538391],
      [121.069919, 14.53839],
      [121.069928, 14.538777] // Close the polygon
    ],
    'C': [
      [121.070769, 14.538362],
      [121.071787, 14.538341],
      [121.07189, 14.538103],
      [121.070762, 14.53812],
      [121.070769, 14.538362] // Close the polygon
    ]
  };

  // Point-in-polygon test using ray casting algorithm
  const pointInPolygon = (point, polygon) => {
    const [x, y] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    return inside;
  };

  // Filter graves that are outside garden polygon
  const filterGravesWithinBounds = (graves, garden) => {
    const polygon = gardenPolygons[garden];
    if (!polygon) return graves;
    
    // Calculate garden bounds for positioning
    const minLng = Math.min(...polygon.map(p => p[0]));
    const maxLng = Math.max(...polygon.map(p => p[0]));
    const minLat = Math.min(...polygon.map(p => p[1]));
    const maxLat = Math.max(...polygon.map(p => p[1]));
    
    const gardenWidth = maxLng - minLng;
    const gardenHeight = maxLat - minLat;
    
    // Real standard grave dimensions (1m × 2m)
    const standardGraveWidth = 0.00000465; // 1m in degrees longitude
    const standardGraveLength = 0.000009;  // 2m in degrees latitude
    
    // Calculate how many graves can fit with standard size
    const maxColumns = Math.floor(gardenWidth / standardGraveWidth);
    const maxRows = Math.floor(gardenHeight / standardGraveLength);
    
    // Use the actual data range to calculate grave size
    const actualRows = Math.max(...graves.map(g => g.row || 0));
    const actualColumns = Math.max(...graves.map(g => g.column || 0));
    const usedRows = Math.min(actualRows, maxRows);
    const usedColumns = Math.min(actualColumns, maxColumns);
    
    const actualGraveWidth = gardenWidth / usedColumns;
    const actualGraveLength = gardenHeight / usedRows;
    
    return graves.filter(grave => {
      const row = grave.row;
      const column = grave.column;
      
      if (!row || !column) return false;
      
      // Calculate grave center position
      const offsetLng = (column - 1) * actualGraveWidth;
      const offsetLat = (row - 1) * actualGraveLength;
      
      const graveCenterLng = minLng + offsetLng + (actualGraveWidth / 2);
      const graveCenterLat = minLat + offsetLat + (actualGraveLength / 2);
      
      // Check if the grave center is within the garden polygon
      const isWithinPolygon = pointInPolygon([graveCenterLng, graveCenterLat], polygon);
      
      if (!isWithinPolygon) {
        console.warn(`Grave ${grave.id} (row ${row}, col ${column}) is outside garden ${garden} polygon - HIDING`);
      }
      
      return isWithinPolygon;
    });
  };

  const fetchLots = async () => {
    try {
      setLoading(true);
      
      // Fetch all graves from unified API (combines old and new systems)
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/public/unified-lots`
      );
      
      // Convert database graves to map-compatible format
      const dbGraves = response.data.map(grave => ({
        _id: grave._id,
        id: grave.id,
        name: grave.name || '',
        birth: grave.birth || '',
        death: grave.death || '',
        status: grave.status,
        coordinates: grave.coordinates,
        garden: grave.garden,
        row: grave.row,
        column: grave.column,
        location: grave.location || `Garden ${grave.garden}`,
        price: grave.price || 8000,
        sqm: grave.sqm || 2.0,
        type: grave.type || 'grave', // Preserve the type field from unified endpoint
        bounds: grave.coordinates ? [
          [grave.coordinates.coordinates[1] - 0.000009, grave.coordinates.coordinates[0] - 0.00000465],
          [grave.coordinates.coordinates[1] + 0.000009, grave.coordinates.coordinates[0] + 0.00000465]
        ] : grave.bounds || [[0, 0], [0, 0]],
        isFromDatabase: true
      }));

      // Load landmarks from landmarks.geojson
      let landmarks = [];
      try {
        const landmarksResponse = await fetch('/data/landmarks.geojson');
        if (landmarksResponse.ok) {
          const landmarksData = await landmarksResponse.json();
          landmarks = (landmarksData.features || []).map(feature => {
            const [lng, lat] = feature.geometry.coordinates;
            return {
              _id: feature.id,
              id: feature.id,
              name: feature.properties.name,
              type: 'landmark',
              category: feature.properties.category,
              status: 'landmark',
              coordinates: {
                type: 'Point',
                coordinates: [lng, lat]
              },
              bounds: [
                [lat - 0.000015, lng - 0.000015],
                [lat + 0.000015, lng + 0.000015]
              ]
            };
          });
          console.log('Loaded landmarks:', landmarks.length);
        }
      } catch (err) {
        console.warn('Failed to load landmarks:', err);
      }

      // Add landmarks to the graves array
      const combinedGraves = [...dbGraves, ...landmarks];
      
      console.log(`Guest Map: ${dbGraves.length} graves from unified endpoint + ${landmarks.length} landmarks = ${combinedGraves.length} total`);
      
      setLots(combinedGraves);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch graves:', err);
      setError('Failed to load graves from server');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLots();
  }, []);

  // Update all 3D garden lots from MapboxMap component
  useEffect(() => {
    const updateAll3DGardenLots = () => {
      if (mapboxMapRef.current) {
        const all3DGardenLots = mapboxMapRef.current.getAll3DGardenLots();
        setAll3DGardenLots(all3DGardenLots);
      }
    };

    // Initial load with a delay to ensure MapboxMap is ready
    const timeoutId = setTimeout(updateAll3DGardenLots, 1000);
    
    return () => clearTimeout(timeoutId);
  }, []); // No dependencies to prevent infinite re-renders

  const handleLotClick = (lot) => {
    setSelectedLot(lot);
  };

  // We don't filter lots for display - MapboxMap handles filtering internally
  // This is just for the info display (exclude landmarks from grave count)
  const getFilteredCount = () => {
    if (mapboxMapRef.current?.getFilteredGraveCount) {
      return mapboxMapRef.current.getFilteredGraveCount();
    }
    return all3DGardenLots.filter(lot => {
      // Only count graves (exclude landmarks and niches)
      if (lot.type !== 'grave') return false;
      if (filter === 'all') return true;
      return lot.status === filter;
    }).length;
  };

  // Get total grave count (only graves, excluding landmarks and niches)
  const getTotalGraveCount = () => {
    // Use lots array which contains the actual grave data from unified endpoint
    return lots.filter(lot => lot.type === 'grave').length;
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setSelectedLot(null); // Clear selection when changing filter
  };

  // Navigation handlers
  const handleNavigationStateChange = (state) => {
    setNavigationState(state);
  };

  const handleNextStep = () => {
    if (mapboxMapRef.current) {
      console.log('Guest map: Next step triggered');
      mapboxMapRef.current.nextStep();
    } else {
      console.error('Guest map: mapboxMapRef.current is null');
    }
  };

  const handleStopNavigation = () => {
    if (mapboxMapRef.current) {
      mapboxMapRef.current.stopNavigation();
    }
  };

  if (loading) {
    return (
      <div className="mapbox-guest-map-view loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading cemetery map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mapbox-guest-map-view guest-map-bottom-spacing">

      {/* Error message */}
      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Map container */}
      <div className="map-container">
        <MapboxMap
          ref={mapboxMapRef}
          lots={lots}
          statusFilter={filter}
          onStatusFilterChange={setFilter}
          selectedLot={selectedLot}
          onLotClick={handleLotClick}
          onMapClick={() => {}} // No map click functionality for guests
          onLotCreate={() => {}} // No lot creation for guests
          isEditing={false} // Guests can't edit
          showRoads={true}
          showColumbarium={true}
          statusFilter={filter}
          onStatusFilterChange={setFilter}
          onNavigationStateChange={handleNavigationStateChange}
          onNextStep={handleNextStep}
          onStopNavigation={handleStopNavigation}
          // Time of day props
          timeOfDay={selectedTimeOfDay}
          // Columbarium grid callback
          onColumbariumGridToggle={setIsColumbariumGridShown}
        />

        {/* Time of Day Selector overlay - floating bottom right */}
        {!isColumbariumGridShown && (
          <div className="time-of-day-overlay">
            <TimeOfDaySelector
              selectedTime={selectedTimeOfDay}
              onTimeChange={setSelectedTimeOfDay}
            />
          </div>
        )}

        {/* Filter controls overlay - floating right */}
         {!isColumbariumGridShown && (
           <div className="map-filter-floating">
             <div className="filter-buttons">
               <button
                 className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                 data-filter="all"
                 onClick={() => handleFilterChange('all')}
               >
                 All Graves
               </button>
               <button
                 className={`filter-btn ${filter === 'available' ? 'active' : ''}`}
                 data-filter="available"
                 onClick={() => handleFilterChange('available')}
               >
                 Available
               </button>
               <button
                 className={`filter-btn ${filter === 'reserved' ? 'active' : ''}`}
                 data-filter="reserved"
                 onClick={() => handleFilterChange('reserved')}
               >
                 Reserved
               </button>
               <button
                 className={`filter-btn ${filter === 'occupied' ? 'active' : ''}`}
                 data-filter="occupied"
                 onClick={() => handleFilterChange('occupied')}
               >
                 Occupied
               </button>
               <button
                 className={`filter-btn ${filter === 'unavailable' ? 'active' : ''}`}
                 data-filter="unavailable"
                 onClick={() => handleFilterChange('unavailable')}
               >
                 Unavailable
               </button>
             </div>
             
             <div className="filter-info">
               Showing {getFilteredCount()} of {getTotalGraveCount()} graves
             </div>
           </div>
         )}
        
        {/* Grave Locator Overlay */}
        <div className="grave-locator-overlay">
          <GraveLocator
            lots={lots}
            onSelect={(lot) => {
              setSelectedLot(lot);
              // Trigger navigation to this grave
              if (mapboxMapRef.current) {
                console.log('Guest map: Navigating to grave:', lot.id);
                mapboxMapRef.current.navigateToGrave(lot);
              } else {
                console.error('Guest map: mapboxMapRef.current is null when trying to navigate');
              }
            }}
            onNavigate={(startLot, endLot) => {
              // Handle point-to-point navigation between lots
              if (mapboxMapRef.current) {
                mapboxMapRef.current.navigateBetweenGraves(startLot, endLot);
              }
            }}
            // Navigation props
            isNavigating={navigationState.isNavigating}
            navigationRoute={navigationState.navigationRoute}
            currentStep={navigationState.currentStep}
            onNextStep={handleNextStep}
            onStopNavigation={handleStopNavigation}
          />
        </div>
      </div>

      {/* Statistics panel */}

      {/* Bottom spacing div */}
      <div style={{ height: '32px', backgroundColor: '#f8f9fa' }}></div>
    </div>
  );
}


